import React from 'react';
import { tokens } from '../theme';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const sizeMap: Record<Size, { h: number; px: number; fs: number }> = {
  sm: { h: 32, px: 14, fs: 13 },
  md: { h: 40, px: 18, fs: 14 },
  lg: { h: 48, px: 24, fs: 15 },
};

function paletteFor(variant: Variant) {
  switch (variant) {
    case 'primary':
      return { bg: tokens.color.night[700], fg: '#fff', border: tokens.color.night[700] };
    case 'secondary':
      return {
        bg: tokens.color.paper['00'],
        fg: tokens.color.ink[700],
        border: tokens.color.line[100],
      };
    case 'ghost':
      return { bg: 'transparent', fg: tokens.color.ink[700], border: 'transparent' };
    case 'danger':
      return { bg: tokens.color.accent.rust, fg: '#fff', border: tokens.color.accent.rust };
  }
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  children,
  style,
  ...rest
}: Props) {
  const dims = sizeMap[size];
  const palette = paletteFor(variant);
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={{
        height: dims.h,
        padding: `0 ${dims.px}px`,
        fontSize: dims.fs,
        fontWeight: 500,
        letterSpacing: 0.02,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        borderRadius: tokens.radius.base,
        opacity: disabled || loading ? 0.5 : 1,
        transition: `transform ${tokens.motion.quick} ${tokens.motion.out}, opacity ${tokens.motion.quick}`,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {loading ? '…' : children}
    </button>
  );
}
